import {
  ATTRIBUTE_SCORE_TONE_BOUNDS,
  SENTIMENT_TONE_HINTS,
  SENTIMENT_TONE_LABELS,
  SentimentTone,
  TONE_METRIC_BOUNDS,
  ToneBounds,
  toneForScore,
  VERDICT_TONE_HINTS,
} from '../GovernanceAnalyticsAiAnswers.utils';
import { TONE_METRIC, ToneMetricName } from '../toneMetric';

/**
 * Turning the tone bands into things a chart can draw.
 *
 * Every band-encoding visual reads from here, and this reads from the shared constants — so moving a
 * boundary is a constants-only edit that updates axis icons, threshold lines, band classification
 * and tooltips everywhere at once. No visual may carry a boundary of its own; a guard test enforces
 * that.
 */

/**
 * Which metrics are BAND-CLASSIFIED, and which bounds each uses.
 *
 * Only the two score metrics have bands. Visibility, share of voice and recommendation are shares
 * of a population — "42% of answers mentioned you" has no favourable or unfavourable reading on its
 * own, so painting sentiment bands behind them would assert a judgement the number does not carry.
 */
export type BandedMetric = ToneMetricName | 'attributeScore';

/**
 * The key the tone metric is banded under, whichever measure `TONE_METRIC` currently selects.
 *
 * A surface asks for bands by this constant rather than by naming a measure, so the switch moves
 * the whole set of band glyphs, threshold lines and classifications with it.
 */
export const TONE_BAND_METRIC: BandedMetric = TONE_METRIC;

/**
 * Each measure of tone on its own bands, and the attribute score on the bands of its own population.
 *
 * The two measures of tone share a 0–1 scale but not a meaning along it: the sentiment's bands are
 * fitted to where its scores pile up, the verdict's sit one per grade of its rubric. Reading one on
 * the other's bands would call a descriptive verdict negative and a merely favourable one excellent.
 */
const BOUNDS_BY_METRIC: Readonly<Record<BandedMetric, ToneBounds>> = {
  ...TONE_METRIC_BOUNDS,
  attributeScore: ATTRIBUTE_SCORE_TONE_BOUNDS,
};

/**
 * What each band means on each metric's scale.
 *
 * The verdict's bands name the grade the answers' average lands on. The sentiment and attribute
 * bands place a score among other answers, and share their wording.
 */
const HINTS_BY_METRIC: Readonly<Record<BandedMetric, Readonly<Record<SentimentTone, string>>>> = {
  sentiment: SENTIMENT_TONE_HINTS,
  verdict: VERDICT_TONE_HINTS,
  attributeScore: SENTIMENT_TONE_HINTS,
};

/** What a band means on a metric's scale, for a tooltip. */
export function bandHint(metric: BandedMetric, tone: SentimentTone): string {
  return HINTS_BY_METRIC[metric][tone];
}

/** A band named and explained on a metric's scale — the sentence every band tooltip carries. */
export function bandSentence(metric: BandedMetric, tone: SentimentTone): string {
  return `${SENTIMENT_TONE_LABELS[tone]} — ${bandHint(metric, tone)}`;
}

/** The bounds a banded metric is read against. */
export function boundsForMetric(metric: BandedMetric): ToneBounds {
  return BOUNDS_BY_METRIC[metric];
}

/** Whether a metric key carries bands at all. */
export function isBandedMetric(metric: string): metric is BandedMetric {
  return metric === 'sentiment' || metric === 'verdict' || metric === 'attributeScore';
}

/** How a value reads on a given metric's bands. */
export function toneForMetric(metric: BandedMetric, value: number | null | undefined): SentimentTone | undefined {
  return toneForScore(value, boundsForMetric(metric));
}

/**
 * The band a 0–100 percentage falls in.
 *
 * Several surfaces (the attribute leaderboard, the competitive scorecard) hold their scores already
 * converted to display percentages. They read against the same bands as everything else, so the
 * conversion belongs here rather than in each caller — a caller doing its own `/ 100` is one edit
 * away from doing its own thresholds too.
 */
export function toneForPercent(metric: BandedMetric, percent: number | null | undefined): SentimentTone | undefined {
  if (typeof percent !== 'number' || !Number.isFinite(percent)) {
    return undefined;
  }
  return toneForMetric(metric, percent / 100);
}

/**
 * Names the band a percentage falls in, with its meaning — for tooltips on surfaces whose colour is
 * already spent on something else.
 *
 * Comparison chips are coloured by how a score sits against a rival, which leaves the absolute
 * reading unsaid: 30 against a competitor's 25 is green for being ahead while both sides are in the
 * Negative band. Spelling the band out in the tooltip closes that gap without a second colour.
 */
export function bandSentenceForPercent(metric: BandedMetric, percent: number | null | undefined): string | undefined {
  const tone = toneForPercent(metric, percent);
  return tone ? bandSentence(metric, tone) : undefined;
}

/** A band a boundary's glyph can name. Never Neutral: every boundary has a side further from it. */
export type BandGlyph = Exclude<SentimentTone, 'neutral'>;

/** One boundary between two bands, positioned for drawing on a 0–100 axis. */
export interface BandThresholdMarker {
  /** The band that begins at this boundary. */
  tone: SentimentTone;
  /**
   * The band the boundary's glyph names: whichever of its two sides lies further from Neutral. A
   * boundary on the way up is named by the band it opens (Good, Excellent), one on the way down by
   * the band beneath it (Negative, Very negative).
   */
  glyph: BandGlyph;
  /** The boundary value on the metric's own 0–1 scale. */
  value: number;
  /** The same position as a percentage, for CSS placement. */
  percent: number;
  /** What the band above this boundary is called. */
  label: string;
  /** What that band means, for a tooltip. */
  hint: string;
}

/** A boundary before it is placed: its bands, and its value where the metric's scale sets one. */
type BoundaryCandidate = Pick<BandThresholdMarker, 'tone' | 'glyph'> & { value?: number };

/**
 * The boundaries to draw for a metric, in ascending order.
 *
 * Every boundary the metric's scale sets is drawn, the floors included: below them the text reads
 * unfavourably, so a reader scanning a chart benefits most from seeing where they sit. A scale with
 * no Very negative band sets no floor beneath Negative, and draws none.
 */
export function bandThresholdMarkers(metric: BandedMetric): BandThresholdMarker[] {
  const bounds = boundsForMetric(metric);
  const candidates: BoundaryCandidate[] = [
    { tone: 'negative', glyph: 'veryNegative', value: bounds.veryNegativeBelow },
    { tone: 'neutral', glyph: 'negative', value: bounds.negativeBelow },
    { tone: 'good', glyph: 'good', value: bounds.goodAtOrAbove },
    { tone: 'excellent', glyph: 'excellent', value: bounds.excellentAtOrAbove },
  ];

  return (
    candidates
      // A boundary the scale does not set is not drawn, and one at the very bottom or top of the axis
      // is not a boundary a reader can see.
      .filter(
        (marker): marker is BoundaryCandidate & { value: number } => marker.value !== undefined && marker.value > 0 && marker.value < 1
      )
      .map((marker) => ({
        ...marker,
        percent: Math.round(marker.value * 100),
        label: SENTIMENT_TONE_LABELS[marker.tone],
        hint: bandHint(metric, marker.tone),
      }))
      .sort((left, right) => left.value - right.value)
  );
}
